import  {Component, Fragment, useState, useEffect, useMemo} from "react";
import ReactDOM from "react-dom";
import classes from "./Archives.module.css";
import moment from "moment";
import { Tabs, Select, Input, Button, Popconfirm, message } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { database } from "../../firebase";
import TextArea from "antd/es/input/TextArea";
import CallRecordingPlayer from '../../components/CallRecordingPlayer/CallRecordingPlayer';

const capitalize = s => {
    if (typeof s !== "string") return "";
    return s.charAt(0).toUpperCase() + s.slice(1);
};

const parseTranscript = (transcriptText) => {
    if (!transcriptText || typeof transcriptText !== 'string') return [];

    const lines = transcriptText.split(/(\d{2}:\d{2}:\d{2})/).filter(Boolean);
    const parsed = [];

    for (let i = 0; i < lines.length; i += 2) {
      if (lines[i].match(/\d{2}:\d{2}:\d{2}/) && lines[i + 1]) {
        const timestamp = lines[i];
        const content = lines[i + 1].trim();

        const speakerMatch = content.match(/^\[(.+?)\]:(.*)/);;
        if (speakerMatch) {
          parsed.push({
            timestamp,
            speaker: speakerMatch[1],
            text: speakerMatch[2].trim()
          });
        }
      }
    }

    return parsed;
};

const TranscriptModal = (props) => {
  const [activeTab, setActiveTab] = useState('transcript');
  const [editableEntries, setEditableEntries] = useState(() => parseTranscript(props.transcript));
  const [editableSummary, setEditableSummary] = useState(props.summary || '');
  const [participantAction, setParticipantAction] = useState('delete');
  const [saving, setSaving] = useState(false);

  // Store original values for history tracking
  const [originalTranscript] = useState(props.transcript || '');
  const [originalSummary] = useState(props.summary || '');
  const [originalParsedEntries] = useState(() => parseTranscript(props.transcript));

  const parsedTranscript = useMemo(() => parseTranscript(props.transcript), [props.transcript]);

  // Admin status
  const isAdmin = props.isTranscriptAdmin !== undefined
    ? Boolean(props.isTranscriptAdmin)
    : Boolean(props.canEditTranscript);

  // ✅ Transcript history comes from props (no useEffect fetch)
  // Expected shape:
  // props.transcriptHistory = {
  //   "1710000000000": { editorName: "ABC", lines: ["...old line..."] },
  //   "1710000100000": { editorName: "DEF", lines: [...] }
  // }
  const transcriptHistoryList = useMemo(() => {
    const raw = props.transcriptHistory || {};
    return Object.entries(raw)
      .map(([tsKey, value]) => ({
        ts: Number(tsKey),
        editorName: value?.editorName || 'Unknown',
        lines: Array.isArray(value?.lines) ? value.lines : []
      }))
      .filter(x => Number.isFinite(x.ts))
      .sort((a, b) => b.ts - a.ts); // newest first
  }, [props.transcriptHistory]);

  const entriesToRender = editableEntries;

  useEffect(() => {
    setEditableEntries(parsedTranscript);
  }, [parsedTranscript]);

  useEffect(() => {
    setEditableSummary(props.summary || '');
  }, [props.summary]);

  const handleEntryChange = (index, field, value) => {
    setEditableEntries(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleDeleteEntry = (index) => {
    const updated = editableEntries.filter((_, i) => i !== index);
    if (updated.length === editableEntries.length) {
      message.info('No entry removed');
      return;
    }
    setEditableEntries(updated);
    message.success('Entry deleted');
  };

  const handleSwapSpeakers = () => {
    let doctorCount = 0;
    let patientCount = 0;
    const updated = editableEntries.map(entry => {
      if (entry.speaker === 'Doctor') {
        doctorCount++;
        return { ...entry, speaker: 'Patient' };
      }
      if (entry.speaker === 'Patient') {
        patientCount++;
        return { ...entry, speaker: 'Doctor' };
      }
      return entry;
    });
    if (doctorCount === 0 && patientCount === 0) {
      message.info('No doctor/patient entries to swap');
      return;
    }
    setEditableEntries(updated);
    message.success(`Swapped speakers: ${doctorCount} Doctor → Patient, ${patientCount} Patient → Doctor`);
  };

  const handleParticipantAction = () => {
    const hasParticipants = editableEntries.some(entry => entry.speaker === 'Participant');
    if (!hasParticipants) {
      message.info('No Participant entries found');
      return;
    }

    if (participantAction === 'delete') {
      const filtered = editableEntries.filter(entry => entry.speaker !== 'Participant');
      const deletedCount = editableEntries.length - filtered.length;
      setEditableEntries(filtered);
      if (deletedCount > 0) {
        message.success(`Deleted ${deletedCount} Participant entr${deletedCount === 1 ? 'y' : 'ies'}`);
      } else {
        message.info('No Participant entries deleted');
      }
      return;
    }

    const targetSpeaker = participantAction === 'doctor' ? 'Doctor' : 'Patient';
    let changedCount = 0;
    const updated = editableEntries.map(entry => {
      if (entry.speaker === 'Participant') {
        changedCount++;
        return { ...entry, speaker: targetSpeaker };
      }
      return entry;
    });
    setEditableEntries(updated);
    if (changedCount > 0) {
      message.success(`Changed ${changedCount} Participant entr${changedCount === 1 ? 'y' : 'ies'} to ${targetSpeaker}`);
    } else {
      message.info('No Participant entries updated');
    }
  };

  const buildTranscriptPayload = () =>
    editableEntries
      .map(entry => `${entry.timestamp} [${entry.speaker}]: ${entry.text}`)
      .join('\n');

  const getChangedOldLines = (before = [], after = []) => {
    const lines = [];
    const maxLen = Math.max(before.length, after.length);

    for (let i = 0; i < maxLen; i++) {
      const b = before[i];
      const a = after[i];

      if (!b) continue;

      if (!a || b.text !== a.text || b.timestamp !== a.timestamp || b.speaker !== a.speaker) {
        lines.push(`${b.timestamp} [${b.speaker}]: ${b.text}`);
      }
    }
    return lines;
  };

  const handleSaveTranscript = async () => {
    if (!props.sessionID || !props.messageKey) {
      message.error('Missing conversation reference for this transcript.');
      return;
    }

    setSaving(true);
    try {
      const reconstructed = buildTranscriptPayload();

      const conversationMessageRef = database.ref(
        `/conversations/${props.sessionID}/${props.messageKey}`
      );

      const existingMessageSnap = await conversationMessageRef.once('value');

      if (!existingMessageSnap.exists()) {
        message.error('Conversation message not found.');
        return;
      }

      const doctorInfo = Array.isArray(props.doctorDetail)
        ? props.doctorDetail[0]
        : props.doctorDetail;

      const timestampKey = Date.now();

      const oldChangedLines = getChangedOldLines(
        originalParsedEntries || [],
        editableEntries || []
      );

      if (oldChangedLines.length > 0) {
        await database
          .ref(`/conversations/${props.sessionID}/${props.messageKey}/transcriptHistory/${timestampKey}`)
          .set({
            editorName: doctorInfo?.doctorName || "Unknown",
            lines: oldChangedLines
          });
      }

      await conversationMessageRef.update({
        transcription: reconstructed,
        transcriptSummary: editableSummary.trim()
      });

      message.success('Transcript saved successfully');
      props.onClose?.();
    } catch (error) {
      console.error('Error saving transcript:', error);
      message.error('Failed to save transcript');
    } finally {
      setSaving(false);
    }
  };

  const hasParticipants = editableEntries.some(entry => entry.speaker === 'Participant');
  const showParticipantTools = isAdmin && hasParticipants;

  const footerButtons = [
    <Button key="cancel" onClick={props.onClose}>
      Close
    </Button>
  ];

  if (activeTab === 'transcript' && isAdmin) {
    footerButtons.push(
      <Button
        key="save"
        type="primary"
        loading={saving}
        onClick={handleSaveTranscript}
        disabled={editableEntries.length === 0}
      >
        Save Changes
      </Button>
    );
  } else if (activeTab === 'summary' && isAdmin) {
    footerButtons.push(
      <Button
        key="save"
        type="primary"
        loading={saving}
        onClick={handleSaveTranscript}
        disabled={!editableSummary.trim()}
      >
        Save Summary
      </Button>
    );
  }

  const modalContent = (
    <div className={classes.transcript_modal_overlay} onClick={props.onClose}>
      <div className={classes.transcript_modal} onClick={e => e.stopPropagation()}>
        <div className={classes.transcript_modal_header}>
          <h2>
            Meeting Transcript {isAdmin && <span style={{ fontSize: '14px', color: '#999' }}>(Admin Mode)</span>}
          </h2>
          <button className={classes.close_btn} onClick={props.onClose}>×</button>
        </div>

        <div className={classes.transcript_tabs}>
          <button
            className={`${classes.tab_btn} ${activeTab === "transcript" ? classes.active_tab : ""}`}
            onClick={() => setActiveTab("transcript")}
          >
            View Transcript
          </button>
          <button
            className={`${classes.tab_btn} ${activeTab === "summary" ? classes.active_tab : ""}`}
            onClick={() => setActiveTab("summary")}
          >
            Summary
          </button>
          <button
            className={`${classes.tab_btn} ${activeTab === "history" ? classes.active_tab : ""}`}
            onClick={() => setActiveTab("history")}
          >
            Transcript History
          </button>
        </div>

        <div className={classes.transcript_content}>
          {activeTab === "transcript" && (
            <div className={classes.transcript_view}>
              {entriesToRender.length > 0 && isAdmin && (
                <div
                  className={classes.transcript_admin_toolbar || ''}
                  style={{
                    marginBottom: showParticipantTools ? '16px' : '8px',
                    borderBottom: showParticipantTools ? '1px solid #f0f0f0' : 'none',
                    paddingBottom: showParticipantTools ? '12px' : 0
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600 }}>Quick Actions:</span>
                    <Button
                      size="small"
                      onClick={handleSwapSpeakers}
                      disabled={editableEntries.length === 0}
                    >
                      Swap Doctor ↔ Patient
                    </Button>
                  </div>

                  {showParticipantTools && (
                    <div
                      style={{
                        marginTop: '16px',
                        border: '1px solid #ff4d4f',
                        borderRadius: '4px',
                        padding: '12px',
                        backgroundColor: '#fff1f0'
                      }}
                    >
                      <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#a8071a' }}>
                        ⚠️ Handle Participants:
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <Select
                          value={participantAction}
                          onChange={setParticipantAction}
                          size="small"
                          style={{ width: 180 }}
                          options={[
                            { value: "doctor", label: "Convert to Doctor" },
                            { value: "patient", label: "Convert to Patient" },
                            { value: "delete", label: "Delete Entries" }
                          ]}
                          getPopupContainer={(node) => node.parentElement}
                        />
                        <Button
                          size="small"
                          type="primary"
                          onClick={handleParticipantAction}
                          danger={participantAction === 'delete'}
                          disabled={saving}
                        >
                          ⚠️ {participantAction === 'delete' ? 'Delete All Participants' : 'Convert All Participants'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {props.sessionID && props.messageKey && (
                <CallRecordingPlayer
                  sessionID={props.sessionID}
                  messageKey={props.messageKey}
                  label="Call Recording"
                />
              )}

              <div className={classes.transcript_entries_container || ''} style={{ marginTop: '16px' }}>
                {entriesToRender.length > 0 ? (
                  entriesToRender.map((item, idx) => {
                    const isParticipantEntry = item.speaker === 'Participant';
                    return (
                      <div
                        key={`${item.timestamp}-${idx}`}
                        className={classes.transcript_item || ''}
                        style={{
                          border: isParticipantEntry ? '2px solid #ff4d4f' : '1px solid #e0e0e0',
                          borderRadius: '6px',
                          padding: '12px',
                          marginBottom: '12px',
                          backgroundColor: isParticipantEntry ? '#fff1f0' : '#fff'
                        }}
                      >
                        <div
                          className={classes.transcript_item_header || ''}
                          style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                            marginBottom: '8px'
                          }}
                        >
                          <span
                            className={classes.transcript_timestamp}
                            style={isParticipantEntry ? { color: '#a8071a', fontWeight: 600 } : {}}
                          >
                            {item.timestamp}
                          </span>

                          {isAdmin ? (
                            <Select
                              value={item.speaker}
                              onChange={(value) => handleEntryChange(idx, 'speaker', value)}
                              size="small"
                              className={classes.transcript_input || ''}
                              style={isParticipantEntry ? { minWidth: 140, borderColor: '#ff4d4f' } : { minWidth: 140 }}
                              options={[
                                { value: "Doctor", label: "Doctor" },
                                { value: "Patient", label: "Patient" },
                                { value: "Participant", label: "Participant" }
                              ]}
                              getPopupContainer={(node) => node.parentElement}
                            />
                          ) : (
                            <span className={classes.transcript_speaker}>[{item.speaker}]:</span>
                          )}

                          {isAdmin && (
                            <Popconfirm
                              title="Delete this entry?"
                              description="Are you sure you want to delete this transcript entry?"
                              onConfirm={() => handleDeleteEntry(idx)}
                              okText="Yes"
                              cancelText="No"
                              disabled={saving}
                            >
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                                disabled={saving}
                              />
                            </Popconfirm>
                          )}
                        </div>

                        {isAdmin ? (
                          <TextArea
                            value={item.text}
                            onChange={(e) => handleEntryChange(idx, 'text', e.target.value)}
                            placeholder="Enter transcript text..."
                            autoSize={{ minRows: 1, maxRows: 8 }}
                            className={classes.transcript_textarea || ''}
                            style={isParticipantEntry ? { borderColor: '#ff4d4f' } : {}}
                          />
                        ) : (
                          <p className={classes.transcript_text}>{item.text}</p>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p className={classes.transcript_empty_state || ''}>
                    No transcript entries found.
                  </p>
                )}
              </div>
            </div>
          )}

          {activeTab === "summary" && (
            <div className={classes.summary_view}>
              {isAdmin ? (
                <TextArea
                  value={editableSummary}
                  onChange={(e) => setEditableSummary(e.target.value)}
                  placeholder="Meeting summary will appear here once available."
                  rows={12}
                  className={classes.transcript_textarea || ''}
                  style={{
                    fontSize: '14px',
                    lineHeight: '1.8',
                    fontFamily: 'monospace'
                  }}
                />
              ) : (
                <p style={{ whiteSpace: 'pre-wrap' }}>{props.summary || "No summary available"}</p>
              )}
            </div>
          )}

          {activeTab === "history" && (
            <div className={classes.history_view}>
              {transcriptHistoryList.length === 0 ? (
                <p className={classes.transcript_empty_state}>No transcript history found.</p>
              ) : (
                transcriptHistoryList.map((h) => (
                  <div
                    key={h.ts}
                    className={classes.history_item}
                  >
                    <div className={classes.history_item_header}>
                      <div className={classes.history_editor_name}>{h.editorName}</div>
                      <div className={classes.history_edit_time}>
                        {new Date(h.ts).toLocaleString()}
                      </div>
                    </div>

                    <div className={classes.history_lines_container}>
                      <div className={classes.history_lines_label}>
                        Edited lines:
                      </div>

                      <div className={classes.history_lines_content}>
                        {h.lines.length > 0 ? (
                          h.lines.map((line, idx) => (
                            <div key={idx} className={classes.history_line}>
                              {line}
                            </div>
                          ))
                        ) : (
                          <div className={classes.history_empty_state}>No changed lines captured.</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div style={{
          borderTop: '1px solid #f0f0f0',
          padding: '12px 16px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '8px'
        }}>
          {footerButtons}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

const SystemMessage = props => {
    const time = props.timestamp
        ? moment(props.timestamp).format("YYYY-MM-DDThh:mm:ssZ")
        : "";
    return (
        <div className={classes.system_message_li}>
            <div className={classes.system_message_wrapper}>
                <div className={classes.system_message_divider}>
                    <span className={classes.system_message_line}></span>
                    <span className={classes.system_message_timestamp}>{time}</span>
                    <span className={classes.system_message_line}></span>
                </div>
                <div className={classes.system_message_content}>
                    <span className={classes.system_message_text}>{props.content}</span>
                    {props.transferredByName && (
                        <span
                            className={classes.system_message_meta}
                            title={props.transferredByID ? `uid: ${props.transferredByID}` : undefined}
                        >
                            Transferred by {props.transferredByName}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

const LeftMessage = props => {
    const [showTranscriptModal, setShowTranscriptModal] = useState(false);

    var name = props.patientName !== undefined ? capitalize(props.patientName) : "Patient";
    return (
        <>
            <div className={classes.msg_body}>
                <div className={classes.msg_name}>
                    <div className={classes.msg_name_text}>{name}</div>
                </div>
                <div className={classes.msg_left_body}>
                    <div className={classes.msg_left_img}>{name.charAt(0)}</div>
                    <div className={classes.msg_text_body}>
                        <div className={classes.msg_text_container}>
                            <div className={classes.msg_bubble_view}>
                                <div className={classes.msg_bubble_container}>
                                    <div>
                                        <div className={classes.msg_bubble_content}>
                                            <div className={classes.msg_bubble_content1}>
                                                <div className={classes.msg_bubble_content2}>
                                                    <div className={classes.msg_bubble_content_row}>
                                                        <div className={classes.msg_bubble_content_view}>
                                                            <div className={classes.msg_bubble_container_content}>
                                                                {props.type === "text" &&
                                                            <span>{props.content}</span>
                                                            }
                                                            {props.type === "image" && (
                                                            <img
                                                                src={props.content} 
                                                                alt=""
                                                                style={{ maxWidth: '100%', height: 'auto' }}
                                                            />
                                                            )}
                                                            {props.type === "application" && (
                                                            <a href={props.content} target="_blank" rel="noopener noreferrer">
                                                            <p>{props.content}</p>
                                                            </a>
                                                            )}
                                                            {props.type === "video" && (
                                                            <video
                                                            src={props.content}
                                                            controls
                                                            style={{ maxWidth: '100%', height: 'auto' }}
                                                          >
                                                            </video>
                                                            )}
                                                            {props.type === "audio" &&
                                                            <audio controls src={props.content}></audio>
                                                            }
                                                            {(props.type === "videoCall" || props.type === "audioCall") && (
                                                                <div className={classes.link_message_container}>
                                                                    <div className={classes.link_text}>
                                                                        {props.content}
                                                                    </div>
                                                                    {props.transcription && props.transcriptSummary && (
                                                                        <button 
                                                                            className={classes.view_transcript_btn}
                                                                            onClick={() => setShowTranscriptModal(true)}
                                                                        >
                                                                            📄 View Transcript
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {props.type === "link" && (
                                                                <div className={classes.link_message_container}>
                                                                    <div className={classes.link_text}>
                                                                        {props.content}
                                                                    </div>
                                                                    {props.transcription && props.transcriptSummary && (
                                                                        <button 
                                                                            className={classes.view_transcript_btn}
                                                                            onClick={() => setShowTranscriptModal(true)}
                                                                        >
                                                                            📄 View Transcript
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                                <div className={classes.message_time_left}>
                                    <span className={classes.timestamp_span_left}>
                                      {moment(props.timestamp).format("YYYY-MM-DDThh:mm:ssZ")}
                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {showTranscriptModal && (
                <TranscriptModal 
                    transcript={props.transcription}
                    summary={props.transcriptSummary}
                    transcriptHistory={props.transcriptHistory}
                    onClose={() => setShowTranscriptModal(false)}
                    sessionID={props.sessionID}
                    messageKey={props.messageKey}
                    doctorDetail={props.doctorDetail}
                    canEditTranscript={props.canEditTranscript}
                    isTranscriptAdmin={props.isTranscriptAdmin}
                />
            )}
        </>
    );
};

const RightMessage = props => {
    const [showTranscriptModal, setShowTranscriptModal] = useState(false);

    return (
        <>
            <div className={classes.msg_body}>
                <div className={classes.msg_right_name}>
                    <div className={classes.msg_name_text}>{props.doctorName}</div>
                </div>
                <div className={classes.msg_right_body}>
                    {props.doctorImg !== undefined && (
                        <img className={classes.msg_right_img} alt="" src={props.doctorImg}/>
                    )}
                    {props.doctorImg === undefined && (
                        <div className={classes.msg_right_img}>{props.doctorName.charAt(0)}</div>
                    )}
                    <div className={classes.msg_right_container}>
                        <div className={classes.msg_right_content}>
                            <div className={classes.msg_right_content1}>
                                <div className={classes.msg_right_content2}>
                                    <div>
                                        <div className={classes.msg_right_bubble_view}>
                                            <div className={classes.msg_right_bubble_container}>
                                                <div className={classes.msg_right_bubble_container_content}>
                                                    <div className={classes.msg_bubble_content_row}>
                                                        <div className={classes.msg_bubble_right}>
                                                            <div className={classes.msg_bubble_container_content}>
                                                                {props.type === "text" &&
                                                            <span>{props.content}</span>
                                                            }
                                                            {props.type === "image" && (
                                                            <img
                                                                src={props.content} 
                                                                alt=""
                                                                style={{ maxWidth: '100%', height: 'auto' }}
                                                            />
                                                            )}
                                                            {props.type === "application" && (
                                                            <a href={props.content} target="_blank" rel="noopener noreferrer">
                                                            <p>{props.content}</p>
                                                            </a>
                                                            )}
                                                            {props.type === "video" && (
                                                            <video
                                                            src={props.content}
                                                            controls
                                                            style={{ maxWidth: '100%', height: 'auto' }}
                                                          >
                                                            </video>
                                                            )}
                                                            {props.type === "audio" &&
                                                            <audio controls src={props.content}></audio>
                                                            }
                                                            {(props.type === "videoCall" || props.type === "audioCall") && (
                                                                <div className={classes.link_message_container}>
                                                                    <div className={classes.link_text}>
                                                                        {props.content}
                                                                    </div>
                                                                    {props.transcription && props.transcriptSummary && (
                                                                        <button 
                                                                            className={classes.view_transcript_btn}
                                                                            onClick={() => setShowTranscriptModal(true)}
                                                                        >
                                                                            📄 View Transcript
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {props.type === "link" && (
                                                                <div className={classes.link_message_container}>
                                                                    <div className={classes.link_text}>
                                                                        {props.content}
                                                                    </div>
                                                                    {props.transcription && props.transcriptSummary && (
                                                                        <button 
                                                                            className={classes.view_transcript_btn}
                                                                            onClick={() => setShowTranscriptModal(true)}
                                                                        >
                                                                            📄 View Transcript
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                                <div className={classes.message_time_right}>
                                    <span className={classes.timestamp_span_right}>
                                      {moment(props.timestamp).format("YYYY-MM-DDThh:mm:ssZ")}
                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {showTranscriptModal && (
                <TranscriptModal 
                    transcript={props.transcription}
                    summary={props.transcriptSummary}
                    transcriptHistory={props.transcriptHistory}
                    onClose={() => setShowTranscriptModal(false)}
                    sessionID={props.sessionID}
                    messageKey={props.messageKey}
                    doctorDetail={props.doctorDetail}
                    canEditTranscript={props.canEditTranscript}
                    isTranscriptAdmin={props.isTranscriptAdmin}
                />
            )}
        </>
    );
};

const TagList = props => {
    return (
        <span>
      <span className={classes.tag_name}>{props.name}</span>
    </span>
    );
};

export default class ArchiveBody extends Component {
    render() {
        // Determine admin status from doctorDetail
        const doctorProfile = Array.isArray(this.props.doctorDetail) ? this.props.doctorDetail[0] : this.props.doctorDetail;
        const canEditTranscript = Boolean(doctorProfile?.role === 'Admin' || doctorProfile?.isadmin || doctorProfile?.isAdmin);
        const isTranscriptAdmin = canEditTranscript;
        const messageList = this.props.archives_messages.map(message => {
            console.log("history",message.receiveMessage.transcriptHistory);

            if (message.receiveMessage.isSystemMessage === true) {
                return (
                    <SystemMessage
                        key={message.receiveMessage.uid || message.receiveMessage.timestamp}
                        timestamp={message.receiveMessage.timestamp}
                        content={message.receiveMessage.content}
                        transferredByName={message.receiveMessage.transferredByName || ''}
                        transferredByID={message.receiveMessage.transferredByID || ''}
                    />
                );
            }

            if (message.receiveMessage.direction === "left") {
                return (
                    <LeftMessage
                        key={message.receiveMessage.uid}
                        timestamp={message.receiveMessage.timestamp}
                        content={message.receiveMessage.content}
                        type={message.receiveMessage.type}
                        patientName={this.props.patientName}
                        transcription={message.receiveMessage.transcription}
                        transcriptSummary={message.receiveMessage.transcriptSummary}
                        sessionID={this.props.sessionID}
                        messageKey={message.receiveMessage.uid || message.receiveMessage.timestamp}
                        doctorDetail={this.props.doctorDetail}
                        canEditTranscript={canEditTranscript}
                        isTranscriptAdmin={isTranscriptAdmin}
                        transcriptHistory={message.receiveMessage.transcriptHistory}
                    />
                );
            } else {
                return (
                    <RightMessage
                        key={message.receiveMessage.uid}
                        timestamp={message.receiveMessage.timestamp}
                        content={message.receiveMessage.content}
                        doctorName={this.props.doctorName}
                        type={message.receiveMessage.type}
                        doctorImg={this.props.doctorImg}
                        transcription={message.receiveMessage.transcription}
                        transcriptSummary={message.receiveMessage.transcriptSummary}
                        sessionID={this.props.sessionID}
                        messageKey={message.receiveMessage.uid || message.receiveMessage.timestamp}
                        doctorDetail={this.props.doctorDetail}
                        canEditTranscript={canEditTranscript}
                        isTranscriptAdmin={isTranscriptAdmin}
                        transcriptHistory={message.receiveMessage.transcriptHistory}
                    />
                );
            }
        });

        const taglist = this.props.tags.map(tag => {
            return <TagList key={tag.id} name={tag.name}/>;
        });
        var inputDate = new Date(
            Boolean(this.props.archive_timestamp) ? this.props.archive_timestamp : null
        );

        var todaysDate = new Date();
        var date = "";
        if (inputDate.setHours(0, 0, 0, 0) === todaysDate.setHours(0, 0, 0, 0)) {
            date = "Today";
        } else {
            date = inputDate.toDateString();
        }
        var doctor_name =
            this.props.headerText.doctor !== undefined ? ` and ${this.props.headerText.doctor}` : "";
        return (
            <div className={classes.main_body}>
                <div className={classes.block}>
                    <div className={classes.main_block}>
                        <div className={classes.header}>
                            <div className={classes.body_header}>
                                <Fragment>
                                    <div className={classes.msg_header_default}>
                    <span className={classes.header_text_top}>
                      <strong>{this.props.headerText.name}</strong>
                        {doctor_name}
                    </span>
                                    </div>
                                </Fragment>
                            </div>
                        </div>
                        <div className={classes.main_body_view}>
                            <div className={classes.main_body_container}>
                                {!this.props.load_messages && (
                                    <Fragment>
                                        <div className={classes.main_body_content}>
                                            {Boolean(this.props.archive_timestamp) && (
                                                <div className={classes.main_body_content_header}>
                                                    <div className={classes.header_line}></div>
                                                    <div className={classes.header_text}>{date}</div>
                                                    <div className={classes.header_line}></div>
                                                </div>
                                            )}
                                            {messageList}
                                        </div>
                                        <div className={classes.tag_list_view}>
                                            <div className={classes.tag_view}>{taglist}</div>
                                        </div>
                                    </Fragment>
                                )}
                                {this.props.load_messages && (
                                    <div className={classes.no_loading}>
                                        <div className={classes.no_loading_view}>
                                            <div className={classes.loading_loader}/>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
